import {useNavigate, useParams} from "react-router-dom";
import {Helmet} from "react-helmet-async";
import {
    Box,
    Button,
    ButtonGroup,
    Card,
    CardContent, Checkbox, CircularProgress,
    Container,
    Input, Modal,
    Stack,
    Table,
    TableBody,
    TableCell, TableContainer, TableHead,
    TableRow, ToggleButton, ToggleButtonGroup,
    Typography
} from "@mui/material";
import Scrollbar from "../components/scrollbar";
import {GeneralLayout} from "../layouts/GeneralLayout";
import TextField from "@mui/material/TextField";
import {useEffect, useState} from "react";
import {useGetSessionForCourseQuery, usePostSaveSessionMutation} from "../store/session/sessionApi";
import PageTitle from "../components/PageTitle";
import {useGetUserDetailsQuery} from "../store/user/userApi";
import {
    useGetStudentsExtraGrcPointsOfCourseQuery,
    useUpdateStudentsExtraGrcPointsOfCourseMutation
} from "../store/grc/grcApi";
import copyObject from "../utils/copyObject";
import FormControlLabel from "@mui/material/FormControlLabel";

export default function CreateSessionPage() {
    const {id, flowNumber} = useParams();
    const selectedSemesterId = localStorage.getItem('selectedSemesterId');
    const dateDefaultValue = 'NaN-NaN-NaN';
    const navigate = useNavigate();
    const [refresh, setRefresh] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [restrictDataSaving, setRestrictDataSaving] = useState(false); // obsolete
    const [saveSession] = usePostSaveSessionMutation();
    const [modalOpen, setModalOpen] = useState(false);
    const {data: userData} = useGetUserDetailsQuery();
    const {data: extraGrcPointsData} = useGetStudentsExtraGrcPointsOfCourseQuery({
        courseId: id, 
        flowNumber,
        semesterId: selectedSemesterId});
    const [copyOfExtraGrcPoints, setCopyOfExtraGrcPoints] = useState(null);
    const {data: sessionData} = useGetSessionForCourseQuery({courseId: id, flowNumber: flowNumber ?? null});
    const [copyOfData, setCopyOfData] = useState({});
    const [saveGrcPointsRequest] = useUpdateStudentsExtraGrcPointsOfCourseMutation();
    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 500,
        overflow: 'scroll',
        height: '90%',
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        pt: 2,
        px: 4,
        pb: 3,
    };

    useEffect(() => {
        if (sessionData) {
            setCopyOfData(copyData(sessionData?.data));
        }
        
        if (extraGrcPointsData){
            setCopyOfExtraGrcPoints(copyObject(extraGrcPointsData));
            console.log('extra', extraGrcPointsData);
        }
    }, [sessionData, extraGrcPointsData]);
    
    function checkIfAllDataIsSet() {
        const controlTypesFilled = copyOfData?.disciplines?.filter((item) => item.controlType === null).length === 0;
        const extraControlTypesFilled = copyOfData?.disciplines?.filter((item) => item.extraControlType === 0).length === 0;
        // костыль
        const datesAreSet = copyOfData?.checksStartDate !== dateDefaultValue 
            && copyOfData?.checksEndDate !== dateDefaultValue
            && copyOfData?.examsStartDate !== dateDefaultValue
            && copyOfData?.examsEndDate !== dateDefaultValue;
        
        setRestrictDataSaving(!controlTypesFilled || !datesAreSet);
    }

    function applyVisualChanges() {
        setRefresh(!refresh);
        checkIfAllDataIsSet();
    }

    function copyData(source) {
        const date = new Date();
        return {
            sessionId: source?.sessionId,
            courseId: parseInt(id, 10),
            checksStartDate: formatDate(source !== null ? new Date(`${source?.checksStartDate}`) : date),
            checksEndDate: formatDate(source !== null ? new Date(`${source?.checksEndDate}`) : date),
            examsStartDate: formatDate(source !== null ? new Date(`${source?.examsStartDate}`) : date),
            examsEndDate: formatDate(source !== null ? new Date(`${source?.examsEndDate}`) : date),
            holidaysStartDate: formatDate(source !== null ? new Date(`${source?.holidaysStartDate}`) : date),
            holidaysEndDate: formatDate(source !== null ? new Date(`${source?.holidaysEndDate}`) : date),
            disciplines: source?.disciplines.map((item) => {
                return copyDiscipline(item);
            }),
            flowNumber: source.flowNumber
        };
    }

    function copyDiscipline(source) {
        return copyObject(source);
    }

    function formatDate(dateObj) {
        if (dateObj === null || dateObj === undefined)
            return null;
        
        return `${dateObj.getFullYear()}-${withZero(dateObj.getMonth() + 1)}-${withZero(dateObj.getDate())}`;
    }

    function withZero(num) {
        return num < 10 ? `0${num}` : num;
    }

    function changeControlType(obj, value) {
        obj.controlType = parseInt(value, 10);
        applyVisualChanges();
    }

    function changeExtraControlType(obj, value) {
        if (obj.extraControlType === parseInt(value, 10))
            obj.extraControlType = 0;
        else
            obj.extraControlType = parseInt(value, 10);

        applyVisualChanges();
    }

    const handleSave = async () => {
        setIsSaving(true);

        if (copyOfData.holidaysStartDate === dateDefaultValue)
            copyOfData.holidaysStartDate = '1970-01-01';

        if (copyOfData.holidaysEndDate === dateDefaultValue)
            copyOfData.holidaysEndDate = '1970-01-01';
        
        await saveSession({session: copyOfData}).unwrap()
            .then((fulfilled) => navigate('/sessions'))
            .catch((ex) => {
                alert('Ошибка');
                console.error(ex);
                setIsSaving(false);
            });
    };

    function numbersOnly(input) {
        if (!input)
            return '';

        const allowedSymbols = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ',', '.'];
        let result = '';
        input.split('').forEach((symbol, index) => {
            if (allowedSymbols.includes(symbol)) {
                result += symbol;
                // if (symbol === ',' || symbol === '.' && input.length === index + 1) {
                //     result += '0';
                // }
            }
        });

        result = result.replace(',', '.');

        return result;
    }
    
    function setCheckedForGroup(group, discipline, checked) {
        if (!discipline.groupsToExclude)
            discipline.groupsToExclude = [];
        
        if (checked) {
            discipline.groupsToExclude = discipline.groupsToExclude.filter((item) => item !== group.groupId);
        } else {
            if (discipline.groupsToExclude.find((item) => item === group.groupId) !== undefined)
                return;
            
            discipline.groupsToExclude.push(group.groupId);
        }
        
        applyVisualChanges();
    }
    
    async function handleSaveExtraGrcPoints() {
        setModalOpen(false);
        
        await saveGrcPointsRequest({data: {
                semesterId: parseInt(selectedSemesterId.toString(), 10),
                students: copyOfExtraGrcPoints?.data ?? []
            }}).unwrap()
            .catch((ex) => {
                alert('Ошибка');
                console.error(ex);
            });
    }

    return <GeneralLayout>
        <PageTitle title={`${sessionData?.sessionId > 0 ? "Редактировать" : "Создать"} сессию`} />

        <Container>
            <Stack
                padding="120px 0px 0px 100px"
                direction="column"
                spacing={2}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                    <Typography variant="h4" gutterBottom>
                        {copyOfData?.sessionId > 0 ? "Редактировать" : "Создать"} сессию для {sessionData?.data?.courseName}
                    </Typography>
                </Stack>

                <Card>
                    <CardContent>
                        <Stack spacing="20px">
                            <Stack direction="row" spacing="10px" alignContent="center" alignItems="center">
                                <Typography>Зачетная неделя с</Typography>
                                <TextField variant="outlined"
                                           type="date"
                                           disabled={copyOfData?.canEdit === false}
                                           value={copyOfData?.checksStartDate}
                                           onChange={(target) => {
                                               copyOfData.checksStartDate = target.target.value;
                                               applyVisualChanges()
                                           }}
                                           placeholder="Дата начала"/>

                                <Typography>по</Typography>

                                <TextField variant="outlined"
                                           type="date"
                                           value={copyOfData?.checksEndDate}
                                           disabled={copyOfData?.canEdit === false}
                                           onChange={(target) => {
                                               copyOfData.checksEndDate = target.target.value;
                                               applyVisualChanges()
                                           }}
                                           placeholder="Дата окончания"/>
                            </Stack>

                            <Stack direction="row" spacing="10px" alignContent="center" alignItems="center">
                                <Typography>Экзамены с</Typography>
                                <TextField variant="outlined"
                                           type="date"
                                           value={copyOfData?.examsStartDate}
                                           disabled={copyOfData?.canEdit === false}
                                           onChange={(target) => {
                                               copyOfData.examsStartDate = target.target.value;
                                               applyVisualChanges()
                                           }}
                                           placeholder="Дата начала"/>

                                <Typography>по</Typography>

                                <TextField variant="outlined"
                                           type="date"
                                           value={copyOfData?.examsEndDate}
                                           disabled={copyOfData?.canEdit === false}
                                           onChange={(target) => {
                                               copyOfData.examsEndDate = target.target.value;
                                               applyVisualChanges()
                                           }}
                                           placeholder="Дата окончания"/>
                            </Stack>

                            <Stack direction="row" spacing="10px" alignContent="center" alignItems="center">
                                <Typography>Каникулы с</Typography>
                                <TextField variant="outlined"
                                           type="date"
                                           value={copyOfData?.holidaysStartDate}
                                           disabled={copyOfData?.canEdit === false}
                                           onChange={(target) => {
                                               copyOfData.holidaysStartDate = target.target.value;
                                               applyVisualChanges()
                                           }}
                                           placeholder="Дата начала"/>

                                <Typography>по</Typography>

                                <TextField variant="outlined"
                                           type="date"
                                           value={copyOfData?.holidaysEndDate}
                                           disabled={copyOfData?.canEdit === false}
                                           onChange={(target) => {
                                               copyOfData.holidaysEndDate = target.target.value;
                                               applyVisualChanges()
                                           }}
                                           placeholder="Дата окончания"/>
                            </Stack>

                            {userData?.userFlags?.grcEnabled
                            ? <Button variant="outlined"
                                      onClick={() => {
                                          setModalOpen(true)
                                      }}
                                      disabled={copyOfData?.canEdit === false}>Установить дополнительные баллы БРС для студентов</Button>
                            : <></>}

                            <Modal
                                open={modalOpen}
                                onClose={() => setModalOpen(false)}
                                aria-labelledby="child-modal-title"
                                aria-describedby="child-modal-description"
                            >
                                <Box sx={{ ...modalStyle, width: 500 }}>
                                    <Stack spacing={2}>
                                        <h2 id="child-modal-title">Дополнительные баллы БРС</h2>
                                        <TableContainer>
                                            <Table size={'small'}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>ФИО</TableCell>
                                                        <TableCell>Баллы</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {copyOfExtraGrcPoints?.data?.sort((l,r) => l.fullName > r.fullName ? 1 : -1).map((item) => {
                                                        return <TableRow>
                                                            <TableCell>{item.fullName}</TableCell>
                                                            <TableCell>
                                                                <TextField variant="outlined"
                                                                           size={'small'}
                                                                           value={item.extraPoints}
                                                                           onChange={(target) => {
                                                                               const newValue = numbersOnly(target.target.value);
                                                                               item.extraPoints = parseFloat(newValue) > 15 ? 15 : newValue;
                                                                               applyVisualChanges()
                                                                           }}
                                                                           placeholder="Баллы"/>
                                                            </TableCell>
                                                        </TableRow>
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        <Button onClick={() => {handleSaveExtraGrcPoints()}}>Сохранить</Button>
                                    </Stack>
                                </Box>
                            </Modal>

                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Дисциплина</TableCell>
                                        <TableCell>Группы</TableCell>
                                        <TableCell>Тип контроля</TableCell>
                                        <TableCell>Дополнительный контроль</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {copyOfData?.disciplines?.map((item) => {
                                        return (
                                            <TableRow>
                                                <TableCell>{item.disciplineName}</TableCell>
                                                <TableCell>{item.groupsOfCourse?.map((group) => {
                                                    return <>
                                                        <FormControlLabel
                                                            control={<Checkbox
                                                                size={"smaller"}
                                                                defaultChecked={!item.groupsToExclude || !item.groupsToExclude.find((g) => g === group.groupId)}
                                                                onChange={(event, checked) => { setCheckedForGroup(group, item, checked); }}/>}
                                                            label={<small>{group.groupName}</small>}/>
                                                        <br />
                                                    </>
                                                })}</TableCell>
                                                <TableCell>
                                                    <ToggleButtonGroup orientation="horizontal"
                                                                       fullWidth
                                                                       exclusive
                                                                       value={item.controlType}
                                                                       onChange={(target) => {
                                                                           changeControlType(item, target.target.value)
                                                                       }}
                                                                       disabled={copyOfData?.canEdit === false}
                                                                       aria-label="Тип контроля"
                                                                       color="primary">
                                                        <ToggleButton value={0} area-label="Экзамен"
                                                                      color="primary">Экзамен</ToggleButton>
                                                        <ToggleButton value={1} area-label="Зачёт"
                                                                      color="primary">Зачёт</ToggleButton>
                                                        <ToggleButton value={2} area-label="Дифф. зачёт"
                                                                      color="primary">Дифф. зачёт</ToggleButton>
                                                    </ToggleButtonGroup>
                                                </TableCell>
                                                <TableCell>
                                                    <ToggleButtonGroup orientation="horizontal"
                                                                       exclusive
                                                                       disabled={copyOfData?.canEdit === false}
                                                                       value={item.extraControlType}
                                                                       onChange={(target) => {
                                                                           changeExtraControlType(item, target.target.value)
                                                                       }}
                                                                       aria-label="Дополнительный контроль"
                                                                       color="primary">
                                                        <ToggleButton value={3} area-label="Экзамен" color="primary">Курсовая
                                                            работа</ToggleButton>
                                                        <ToggleButton value={4} area-label="Зачёт" color="primary">Курсовой
                                                            проект</ToggleButton>
                                                    </ToggleButtonGroup>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                            
                            <Stack justifyContent="space-between">
                                {
                                    !isSaving
                                        ? <Button variant="contained" 
                                                  onClick={handleSave}
                                                  disabled={copyOfData?.canEdit === false}
                                            >Сохранить</Button>
                                        : <CircularProgress/>
                                }
                            </Stack>
                            

                        </Stack>
                    </CardContent>
                </Card>
                
                <br />
            </Stack>
        </Container>
    </GeneralLayout>
}